import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import PermissionControl from "components/Utils/PermissionControl";

function MerchantCategoryCodeConfig() {
  const api = useApi();

  const [mccList, setMccList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    mccCode: "",
    description: "",
  });

  // ================== TABLE COLUMNS ==================
  const columns = [
    { Header: "MCC Code", accessor: "mccCode", width: "20%", align: "left" },
    { Header: "Description", accessor: "description", width: "50%", align: "left" },
    {
      Header: "Action",
      accessor: "action",
      width: "15%",
      align: "center",
    },
  ];

  // ================== FETCH MCC LIST ==================
  const fetchMccList = () => {
    setLoading(true);
    api
      .get("/mcc/all")
      .then((res) => {
        if (Array.isArray(res.data)) {
          // const rows = res.data.map((item) => ({
          //   ...item,

          //   edit: (
          //     <PermissionControl
          //       permission={{ restrict: true, module: "MCC", subModule: "Update" }}
          //     >
          //       <MDButton
          //         size="small"
          //         color="info"
          //         variant="contained"
          //         onClick={() => handleEdit(item)}
          //       >
          //         Edit
          //       </MDButton>
          //     </PermissionControl>
          //   ),

          //   delete: (
          //     <PermissionControl
          //       permission={{ restrict: true, module: "MCC", subModule: "Delete" }}
          //     >
          //       <MDButton
          //         size="small"
          //         color="error"
          //         variant="contained"
          //         onClick={() => handleDelete(item.mccCode)}
          //       >
          //         Delete
          //       </MDButton>
          //     </PermissionControl>
          //   ),
          // }));

          const rows = res.data.map((item) => ({
            ...item,

            action: (
              <MDBox display="flex" justifyContent="center" gap={1}>
                {/* EDIT */}
                <PermissionControl
                  permission={{ restrict: true, module: "MCC", subModule: "Update" }}
                >
                  <IconButton size="small" color="info" onClick={() => handleEdit(item)}>
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                </PermissionControl>

                {/* DELETE */}
                <PermissionControl
                  permission={{ restrict: true, module: "MCC", subModule: "Delete" }}
                >
                  <IconButton size="small" color="error" onClick={() => handleDelete(item.mccCode)}>
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </PermissionControl>
              </MDBox>
            ),
          }));
          setMccList(rows);
        } else {
          toast.error("Failed to load MCC list");
        }
      })
      .catch(() => toast.error("Failed to load MCC list"))
      .finally(() => setLoading(false));
  };

  // ================== HANDLERS ==================
  const handleCreate = () => {
    setIsEdit(false);
    setFormData({ mccCode: "", description: "" });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setFormData({
      mccCode: item.mccCode,
      description: item.description,
    });
    setOpenDialog(true);
  };

  const handleDelete = (mccCode) => {
    if (!window.confirm(`Are you sure you want to delete MCC ${mccCode}?`)) return;

    api
      .delete(`/mcc/delete/${mccCode}`)
      .then(() => {
        toast.success("MCC deleted successfully");
        fetchMccList();
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          toast.error("This MCC is attached to one or more merchants. Please remove the connection before deleting this MCC.");
        } else {
          toast.error("Failed to delete MCC");
        }
      });
  };

  const handleSubmit = () => {
    if (!formData.mccCode || !formData.description) {
      toast.error("All fields are required");
      return;
    }

    setLoading(true);

    const request = {
      mccCode: formData.mccCode,
      description: formData.description,
    };

    const apiCall = isEdit
      ? api.put(`/mcc/update/${formData.mccCode}`, request)
      : api.post("/mcc/create", request);

    apiCall
      .then(() => {
        toast.success(`MCC ${isEdit ? "updated" : "created"} successfully`);
        setOpenDialog(false);
        fetchMccList();
      })
      .catch(() => toast.error("Failed to create merchant Category"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMccList();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between">
                <MDTypography variant="h6">Merchant Category Code (MCC)</MDTypography>

                <PermissionControl
                  permission={{ restrict: true, module: "MCC", subModule: "Create" }}
                >
                  <MDButton color="info" variant="contained" onClick={handleCreate}>
                    Add MCC
                  </MDButton>
                </PermissionControl>
              </MDBox>

              <MDBox px={3} pb={3}>
                <DataTable
                  table={{ columns, rows: mccList }}
                  canSearch
                  entriesPerPage
                  isSorted
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ================== DIALOG ================== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? "Update MCC" : "Create MCC"}</DialogTitle>

        <DialogContent>
          <MDBox mt={2}>
            <TextField
              fullWidth
              label="MCC Code"
              value={formData.mccCode}
              disabled={isEdit}
              onChange={(e) => setFormData({ ...formData, mccCode: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDialog(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default MerchantCategoryCodeConfig;
